import React, { useState, useCallback, useRef, useLayoutEffect, useEffect } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { ChevronLeft, ChevronRight, MapPin } from "lucide-react";

const topDestinations = [
  {
    id: 1,
    name: "Maldives",
    subtitle: "Overwater Paradise",
    image: "https://images.unsplash.com/photo-1697898109604-e06e88b15271?w=1920&q=80",
  },
  {
    id: 2,
    name: "Dubai",
    subtitle: "City of Gold",
    image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=80",
  },
  {
    id: 3,
    name: "Santorini",
    subtitle: "Aegean Dream",
    image: "https://images.pexels.com/photos/161342/greece-santorini-architecture-island-161342.jpeg?w=1920",
  },
  {
    id: 4,
    name: "Bali",
    subtitle: "Island of Gods",
    image: "https://images.unsplash.com/photo-1540541338287-41700207dee6?w=1920&q=80",
  },
  {
    id: 5,
    name: "Paris",
    subtitle: "City of Lights",
    image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1920&q=80",
  },
  {
    id: 6,
    name: "Tokyo",
    subtitle: "Where Tradition Meets Future",
    image: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1920&q=80",
  },
];

export const TopDestinationsBar = () => {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const initialized = useRef(false);

  useLayoutEffect(() => {
    if (!api || initialized.current) return;
    initialized.current = true;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  // Auto-play
  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      api.scrollNext();
    }, 5000);
    return () => clearInterval(interval);
  }, [api]);

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  return (
    <section className="pt-20">
      <div className="relative w-full h-[50vh] sm:h-[55vh] lg:h-[60vh]">
        <Carousel
          setApi={setApi}
          opts={{
            align: "start",
            loop: true,
          }}
          className="w-full h-full"
        >
          <CarouselContent className="h-full ml-0">
            {topDestinations.map((destination) => (
              <CarouselItem
                key={destination.id}
                className="pl-0 h-full"
              >
                <div className="relative w-full h-full">
                  {/* Full Width Image */}
                  <img
                    src={destination.image}
                    alt={destination.name}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-r from-foreground/30 via-transparent to-foreground/30" />
                  
                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 lg:p-16">
                    <div className="max-w-7xl mx-auto">
                      <div className="flex items-center gap-2 text-secondary mb-3">
                        <MapPin className="w-5 h-5" />
                        <span className="text-sm sm:text-base font-medium uppercase tracking-wider">
                          {destination.subtitle}
                        </span>
                      </div>
                      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-background">
                        {destination.name}
                      </h2>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        {/* Navigation Arrows */}
        <button
          onClick={scrollPrev}
          className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 p-3 sm:p-4 rounded-full bg-background/20 backdrop-blur-sm border border-background/30 text-background hover:bg-secondary hover:text-foreground hover:border-secondary transition-all duration-300"
          aria-label="Previous destination"
        >
          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        <button
          onClick={scrollNext}
          className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 p-3 sm:p-4 rounded-full bg-background/20 backdrop-blur-sm border border-background/30 text-background hover:bg-secondary hover:text-foreground hover:border-secondary transition-all duration-300"
          aria-label="Next destination"
        >
          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>

        {/* Dots Indicator */}
        <div className="absolute bottom-6 sm:bottom-10 right-6 sm:right-10 flex items-center gap-2">
          {Array.from({ length: count }).map((_, index) => (
            <button
              key={index}
              onClick={() => api?.scrollTo(index)}
              className={`h-2 sm:h-3 rounded-full transition-all duration-300 ${
                index === current
                  ? "w-8 sm:w-10 bg-secondary"
                  : "w-2 sm:w-3 bg-background/50 hover:bg-background/70"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Top Destinations Label */}
        <div className="absolute top-4 sm:top-6 left-4 sm:left-8 flex items-center gap-2 px-4 py-2 rounded-full bg-background/20 backdrop-blur-sm border border-background/30">
          <MapPin className="w-4 h-4 text-secondary" />
          <span className="text-sm font-semibold text-background">Top Destinations</span>
        </div>
      </div>
    </section>
  );
};

export default TopDestinationsBar;
